import axios from "axios";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_c595e040-51d4-4a47-a393-235661805ccd/artifacts/c4d3xfwc_Artboard%204.png";

const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("bhega_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
    // Tag the request so the response interceptor can distinguish
    // "I had a token and it was rejected" (session expiry) from an
    // anonymous 401 (bad credentials on a public endpoint).
    config._hadToken = true;
  }
  return config;
});

// A single-flight guard — multiple parallel requests can all 401 at once;
// we only want ONE logout + redirect dispatched.
let sessionExpiryFired = false;

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const url = (error?.config?.url || "").toString();
    const hadToken = !!error?.config?._hadToken;

    // Only treat 401 as a *session expiry* when we actually sent an auth
    // header AND we weren't trying to log in or verify an OTP. Auth
    // endpoints return 401 on bad credentials, which is NOT session
    // expiry.
    const isAuthEndpoint =
      url.includes("/auth/otp/") ||
      url.includes("/auth/admin-login") ||
      url.includes("/auth/login");

    if (status === 401 && hadToken && !isAuthEndpoint && !sessionExpiryFired) {
      sessionExpiryFired = true;
      // Clear persisted auth so a reload shows a clean, logged-out shell.
      try {
        localStorage.removeItem("bhega_token");
        localStorage.removeItem("bhega_user");
      } catch (_) { /* storage may be unavailable in private mode */ }

      // Hand off to the AuthProvider — it will finalise logout + navigate.
      try {
        window.dispatchEvent(new CustomEvent("bhega:session-expired"));
      } catch (_) { /* noop */ }

      // Reset the guard after a tick so later navigation to login still works
      // if the user re-authenticates and immediately hits a 401 again.
      setTimeout(() => { sessionExpiryFired = false; }, 5000);
    }

    return Promise.reject(error);
  },
);

export default api;

export function inrFormat(num) {
  if (num == null) return "—";
  if (num >= 10000000) return `₹${(num / 10000000).toFixed(num % 10000000 === 0 ? 0 : 2)} Cr`;
  if (num >= 100000) return `₹${(num / 100000).toFixed(num % 100000 === 0 ? 0 : 2)} L`;
  if (num >= 1000) return `₹${(num / 1000).toFixed(0)}k`;
  return `₹${num}`;
}

export function categoryLabel(c) {
  if (!c) return "";
  const map = { car: "Cars", bike: "Bikes", real_estate: "Real Estate", electronics: "Electronics", mobile: "Mobiles" };
  if (map[c]) return map[c];
  return c
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

/**
 * Convert an axios error (including FastAPI validation errors that come as an
 * array of `{type, loc, msg, input, url}` objects) into a plain string suitable
 * for toasts / React text nodes.
 */
export function apiErrorMessage(err, fallback = "Something went wrong") {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((d) => {
      if (typeof d === "string") return d;
      const field = Array.isArray(d?.loc) ? d.loc.slice(-1)[0] : (d?.loc || "field");
      return `${field}: ${d?.msg || "invalid"}`;
    }).join(" · ");
  }
  try { return JSON.stringify(detail); } catch { return fallback; }
}


/**
 * Build the canonical URL for a product. Slug-based for SEO:
 * `iPhone 17 Pro Max` → `/iphone-17-pro-max`. Falls back to the catalog
 * pre-filtered by the product name if no slug is available.
 */
export function productUrl(p) {
  if (!p) return "/browse";
  const slug = p.slug || (p.product || p.name || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  if (slug) return `/${slug}`;
  const cat = p.category || "automobile";
  const q = encodeURIComponent(p.product || p.name || "");
  return `/${cat}?q=${q}`;
}

export function categoryUrl(slug) {
  // /c/<slug> retired — top-level slug is the canonical URL now.
  return `/${slug || "browse"}`;
}
